import {
  Box,
  CloseButton,
  Flex,
  Switch,
  type BoxProps,
  Icon,
} from "@chakra-ui/react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  useColorMode,
  useColorModeValue,
} from "../../common/components/chakra/color-mode.tsx";
import Logo from "../Logo.tsx";
import { FiLogIn, FiLogOut } from "react-icons/fi";
import { isBrowser } from "react-device-detect";
import { LinkItem, type LinkItemProps } from "./NavItems";
import { useNavigate } from "react-router-dom";

interface SidebarProps extends BoxProps {
  onClose: () => void;
  linkItems: Array<LinkItemProps>;
  rest?: Record<string, unknown>;
}

const SidebarContent = ({ onClose, linkItems, ...rest }: SidebarProps) => {
  const { user, logout } = useAuth0();
  const colorMode = useColorMode();
  const navigate = useNavigate();
  return (
    <Box
      bg={useColorModeValue("white", "gray.900")}
      borderRight="1px solid"
      borderRightColor={useColorModeValue("gray.200", "gray.700")}
      w={isBrowser ? "60" : "100%"}
      pl={0}
      pb={2}
      style={{
        flex: 1,
        display: "flex",
        flexFlow: "column nowrap",
        minHeight: "0",
      }}
      {...(rest as BoxProps)}
    >
      <Flex
        h="4rem"
        alignItems="center"
        mx="8"
        mb={4}
        pt={5}
        justifyContent="space-between"
      >
        <div></div>
        <Flex
          style={{ flex: 8 }}
          h={"inherit"}
          w={"100%"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <Logo style={{ height: "95%" }} />
        </Flex>
        <CloseButton display={!isBrowser ? "flex" : "none"} onClick={onClose} />
      </Flex>
      <div
        style={{
          display: "flex",
          flexFlow: "column nowrap",
          justifyContent: "space-between",
          height: "100%",
          flex: 1,
          minHeight: "0",
        }}
      >
        {/* Scrollable links: fills remaining space */}
        <div style={{ flex: 1, minHeight: "0", overflowY: "auto" }}>
          {!user && (
            <>
              <Box
                onClick={() => {
                  const returnTo =
                    window.location.pathname + window.location.search;
                  navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
                }}
                as={"a"}
                style={{ textDecoration: "none" }}
                _focus={{ boxShadow: "none" }}
              >
                <Flex
                  align="center"
                  p="4"
                  mx="4"
                  borderRadius="lg"
                  role="group"
                  cursor="pointer"
                  _hover={{ bg: "cyan.600", color: "white" }}
                >
                  <Icon
                    mr="4"
                    fontSize="16"
                    _groupHover={{ color: "white" }}
                    as={FiLogIn}
                  />
                  Login
                </Flex>
              </Box>
            </>
          )}
          {user && (
            <>
              {linkItems.map((link) => (
                <LinkItem key={link.name} link={link} onClose={onClose} />
              ))}
            </>
          )}
        </div>

        <div
          style={{
            height: "fit-content",
            display: "flex",
            flexFlow: "column",
            justifyContent: "flex-end",
            paddingTop: "10px",
          }}
        >
          <Flex justifyContent={"center"} w={"100%"}>
            <Switch.Root
              size={"lg"}
              onCheckedChange={() => colorMode.toggleColorMode()}
              pb={3}
            >
              <Switch.HiddenInput />
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <Switch.Label>
                {colorMode.colorMode === "light" ? "🌙" : "☀️"}
              </Switch.Label>
            </Switch.Root>
          </Flex>
          {user && (
            <Box
              onClick={() => {
                logout();
              }}
              as={"a"}
              style={{ textDecoration: "none" }}
              _focus={{ boxShadow: "none" }}
            >
              <Flex
                align="center"
                p="4"
                mx="4"
                borderRadius="lg"
                role="group"
                cursor="pointer"
                _hover={{ bg: "orange.600", color: "black" }}
              >
                <Icon
                  mr="4"
                  fontSize="16"
                  _groupHover={{ color: "white" }}
                  as={FiLogOut}
                />
                Logout
              </Flex>
            </Box>
          )}
        </div>
      </div>
    </Box>
  );
};

export default SidebarContent;
